<script setup>
import FAQItem from 'components/faq-item/FAQItem.vue';
import { usePageMeta } from 'composables/page-meta';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { sectionCountLabel } from 'src/services/user-profile-service';
import { buildPageMeta } from 'src/utils/page-meta-util';

const faqGroups = [
    {
        id: 'market-evidence',
        title: 'Market evidence',
        hasNotesLink: true,
        questions: [
            {
                question: 'What is Xprt Dev?',
                answer: 'Xprt Dev is an evidence-led view of the Australian ICT hiring market. It summarises active listings in '
                    + 'Xprt Jobs inventory, including demand, advertised pay, employment types, work arrangements, '
                    + 'advertising organisations and active specialisations.'
            },
            {
                question: 'Does Xprt Dev cover the whole Australian market?',
                answer: 'No. The figures describe active roles observed in Xprt Jobs inventory. They are not a census of every '
                    + 'Australian vacancy and they are not a forecast. The methodology explains coverage, calculation, '
                    + 'suppression and freshness limits.'
            },
            {
                question: 'How are Xprt Dev and Xprt Jobs related?',
                answer: 'Xprt Dev explains ICT market signals. Xprt Jobs owns job discovery, applications and employer tools. '
                    + 'Market pages link to the matching Xprt Jobs category when you are ready to browse current roles.'
            }
        ]
    },
    {
        id: 'accounts-and-profiles',
        title: 'Accounts and profiles',
        questions: [
            {
                question: 'Do I need an account to view the market?',
                answer: 'No. Public market pages and the methodology are available without logging in. An account lets you '
                    + 'save one ICT classification and one Australian location as My Market.'
            },
            {
                question: 'Do I have to publish a career profile?',
                answer: 'No. The career profile is optional and independent of My Market. If you build one, you can choose '
                    + 'public, logged-in or restricted access and can create protected links for selective sharing.'
            },
            {
                question: 'Will my profile apply to jobs for me?',
                answer: 'No. Shared basic details may help prefill only roles that accept applications through Xprt Jobs. '
                    + 'External employer or recruiter application systems remain separate, and Xprt Dev does not own those '
                    + 'applications.'
            },
            {
                question: 'Why can\'t I see contributions on my profile?',
                answer: 'First enable \u201cShow my public contributions\u201d in the career-profile editor. Xprt displays only '
                    + 'contribution information that GitHub or GitLab makes public, and caches that data for six hours. '
                    + 'Private repository contents are not requested.'
            }
        ]
    },
    {
        id: 'help',
        title: 'Getting help',
        questions: [
            {
                question: 'How can I report a problem or suggest an improvement?',
                answer: 'Logged-in members can open Help from the user menu, then choose Report a bug or Request a feature. '
                    + 'For a questionable profile, use the report control on that profile so the review has the correct context.'
            }
        ]
    }
];

usePageMeta(buildPageMeta({
    title: 'Tech Market & Career Profile FAQs',
    description: 'Answers about Australian ICT market data, advertised salaries, My Market watches, career-profile '
        + 'privacy and how Xprt Dev works with Xprt Jobs.',
    canonicalUrl: 'https://xprt.dev/faqs'
}));
</script>

<template>
    <main class="faqs-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <div class="hero-copy q-py-lg">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">FAQs · Market evidence, accounts and profiles</p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">Common questions, answered.</h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-my-none">
                        What Xprt Dev measures, where its boundaries sit, and what an account adds.
                    </p>
                </div>
            </div>
        </header>
        <section
            v-for="(group, index) in faqGroups"
            :key="group.id"
            :aria-labelledby="`${group.id}-heading`"
            :class="{ 'market-section-rule': index < faqGroups.length - 1 }"
            data-test="faq-group"
        >
            <div class="section-split container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs" data-test="faq-group-count">
                        {{ sectionCountLabel(group.questions.length, 'question') }}
                    </p>
                    <h2 :id="`${group.id}-heading`" class="text-h4 text-weight-bold q-my-none">{{ group.title }}</h2>
                    <q-btn
                        v-if="group.hasNotesLink"
                        :to="AppRoute.build(Route.Methodology)"
                        class="q-mt-lg"
                        color="primary"
                        label="Read the notes"
                        no-caps
                        outline
                    />
                </div>
                <div>
                    <FAQItem v-for="item in group.questions" :key="item.question" :question="item.question">{{ item.answer }}</FAQItem>
                </div>
            </div>
        </section>
    </main>
</template>

<style lang="scss" scoped>
.faqs-page {
    .hero-copy {
        max-width: 880px;
    }

    .hero-deck {
        max-width: 720px;
    }
}
</style>
